import React from 'react';
import { Settings, FileSpreadsheet, RefreshCw, Power } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const statusColors = {
  'В обработке': 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35',
  'Ожидает': 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35',
  'Задерживается': 'bg-orange-500/15 text-orange-300 border-orange-500/35',
  'Доставлено': 'bg-green-500/15 text-green-300 border-green-500/35',
  'Отменена': 'bg-red-500/15 text-red-300 border-red-500/35',
};

const statusIcons = {
  'В обработке': '📦',
  'Ожидает': '📦',
  'Задерживается': '⏳',
  'Доставлено': '✅',
  'Отменена': '❌',
};

const ManagerDeliveryPage = () => {
  const { initData, tg } = useTelegram();

  const [activeCountryTab, setActiveCountryTab] = React.useState('KG');

  const [settings, setSettings] = React.useState(null);
  const [settingsLoading, setSettingsLoading] = React.useState(true);
  const [settingsError, setSettingsError] = React.useState('');

  const [deadlineInput, setDeadlineInput] = React.useState('');
  const [enableLoading, setEnableLoading] = React.useState(false);
  const [enableError, setEnableError] = React.useState('');

  const [deliveries, setDeliveries] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState('');

  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [deliverySearch, setDeliverySearch] = React.useState('');

  const [exportLoading, setExportLoading] = React.useState(false);
  const [exportError, setExportError] = React.useState('');
  const [exportSuccess, setExportSuccess] = React.useState('');
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = 10;
  const [deletingId, setDeletingId] = React.useState(null);
  const [updatingId, setUpdatingId] = React.useState(null);

  const filteredDeliveries = React.useMemo(() => {
    const q = String(deliverySearch || '').trim().toLowerCase();
    if (!q) return deliveries;
    const queryDigits = q.replace(/\D/g, '');
    if (q.length < 2 && queryDigits.length < 2) return deliveries;

    return deliveries.filter((d) => {
      const fullName = String(d?.full_name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const phoneDigits = String(d?.phone_digits || '').replace(/\D/g, '');
      const rawPhone = String(d?.phone_digits || '').toLowerCase();
      const words = q.split(/\s+/).filter((part) => part.length >= 2);
      const nameMatches = words.length ? words.every((part) => fullName.includes(part)) : fullName.includes(q);
      return nameMatches || rawPhone.includes(q) || (!!queryDigits && phoneDigits.includes(queryDigits));
    });
  }, [deliveries, deliverySearch]);

  const loadSettings = React.useCallback(async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setSettingsError('');
    setSettingsLoading(true);
    try {
      const r = await apiPost('/api/manager/delivery/settings/get', { initData: initDataRaw, country: activeCountryTab });
      setSettings(r?.settings || null);
    } catch (e) {
      setSettingsError(e?.message || 'Ошибка');
    } finally {
      setSettingsLoading(false);
    }
  }, [initData?.initData, activeCountryTab]);

  const loadDeliveries = React.useCallback(async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setListError('');
    setListLoading(true);
    try {
      const body = { initData: initDataRaw, country: activeCountryTab };
      if (dateFrom && dateTo) {
        body.dateFrom = dateFrom;
        body.dateTo = dateTo;
      }
      const r = await apiPost('/api/manager/delivery/list', body);
      setDeliveries(Array.isArray(r?.deliveries) ? r.deliveries : []);
      setPage(0);
    } catch (e) {
      setListError(e?.message || 'Ошибка');
    } finally {
      setListLoading(false);
    }
  }, [initData?.initData, dateFrom, dateTo, activeCountryTab]);

  React.useEffect(() => {
    loadSettings();
    loadDeliveries();
  }, [loadSettings, loadDeliveries]);

  const handleEnable = async () => {
    setEnableError('');
    const initDataRaw = initData?.initData;
    if (!initDataRaw) { setEnableError('Telegram initData ещё не готов'); return; }
    if (activeCountryTab === 'KG' && !deadlineInput) { setEnableError('Укажите время'); return; }

    setEnableLoading(true);
    try {
      await apiPost('/api/manager/delivery/enable', { initData: initDataRaw, deadlineTime: deadlineInput, country: activeCountryTab });
      await loadSettings();
      setDeadlineInput('');
    } catch (e) {
      setEnableError(e?.message || 'Ошибка');
    } finally {
      setEnableLoading(false);
    }
  };

  const handleDisable = async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;

    const ok = await new Promise((resolve) => {
      if (tg?.showPopup) {
        tg.showPopup(
          {
            title: 'Подтверждение',
            message: 'Выключить доставку?',
            buttons: [
              { id: 'cancel', type: 'cancel', text: 'Отмена' },
              { id: 'ok', type: 'destructive', text: 'Выключить' },
            ],
          },
          (buttonId) => resolve(buttonId === 'ok')
        );
        return;
      }
      resolve(window.confirm('Выключить доставку?'));
    });
    if (!ok) return;

    setEnableLoading(true);
    try {
      await apiPost('/api/manager/delivery/disable', { initData: initDataRaw, country: activeCountryTab });
      await loadSettings();
    } catch (e) {
      setEnableError(e?.message || 'Ошибка');
    } finally {
      setEnableLoading(false);
    }
  };

  const handleExport = async () => {
    setExportError('');
    setExportSuccess('');
    const initDataRaw = initData?.initData;
    if (!initDataRaw) { setExportError('Telegram initData ещё не готов'); return; }

    setExportLoading(true);
    try {
      const body = { initData: initDataRaw, country: activeCountryTab };
      if (dateFrom && dateTo) {
        body.dateFrom = dateFrom;
        body.dateTo = dateTo;
      }

      const res = await fetch(getApiPath('/api/manager/delivery/export/excel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Request failed: ${res.status}`);
      }

      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] || 'deliveries.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportSuccess(`Файл ${fileName} скачан. Проверьте папку «Загрузки» на устройстве.`);
    } catch (e) {
      setExportError(e?.message || 'Ошибка');
    } finally {
      setExportLoading(false);
    }
  };

  const isEnabled = settings?.enabled === 1;

  const STATUSES = ['В обработке', 'Ожидает', 'Задерживается', 'Доставлено'];

  const handleStatusChange = async (deliveryId, newStatus) => {
    const ok = await new Promise((resolve) => {
      if (tg?.showPopup) {
        tg.showPopup(
          {
            title: 'Подтверждение',
            message: `Изменить статус на "${newStatus}"?`,
            buttons: [
              { id: 'cancel', type: 'cancel', text: 'Отмена' },
              { id: 'ok', type: 'default', text: 'Да' },
            ],
          },
          (buttonId) => resolve(buttonId === 'ok')
        );
        return;
      }
      resolve(window.confirm(`Изменить статус на "${newStatus}"?`));
    });
    if (!ok) return;

    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;

    setUpdatingId(deliveryId);
    try {
      await apiPost('/api/courier/delivery/status', { initData: initDataRaw, deliveryId, newStatus });
      await loadDeliveries();
    } catch (e) {
      setListError(e?.message || 'Ошибка обновления статуса');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-2xl border border-stroke bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <Settings size={22} className="text-neon-yellow" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Управление доставкой</h1>
            <p className="text-xs text-text-secondary">Настройки и отчёты</p>
          </div>
        </div>

        {/* Settings card */}
        <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 mb-4">
          {/* Country tab switcher */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setActiveCountryTab('KG')}
              className={`flex-1 h-9 rounded-xl font-semibold text-sm transition ${
                activeCountryTab === 'KG'
                  ? 'bg-neon-blue/20 border border-neon-blue/50 text-neon-blue'
                  : 'border border-stroke bg-white/5 text-text-muted hover:bg-white/8'
              }`}
            >
              🇰🇬 KG
            </button>
            <button
              type="button"
              onClick={() => setActiveCountryTab('KZ')}
              className={`flex-1 h-9 rounded-xl font-semibold text-sm transition ${
                activeCountryTab === 'KZ'
                  ? 'bg-neon-blue/20 border border-neon-blue/50 text-neon-blue'
                  : 'border border-stroke bg-white/5 text-text-muted hover:bg-white/8'
              }`}
            >
              🇰🇿 KZ
            </button>
          </div>

          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Power size={14} />
            Окно доставки
          </div>

          {settingsLoading ? <div className="text-xs text-text-secondary">Загрузка…</div> : null}
          {settingsError ? <div className="text-xs text-red-300 mb-2">{settingsError}</div> : null}

          {settings && !settingsLoading ? (
            <div className="mb-3">
              <div className={`text-xs font-semibold px-2 py-1 rounded-lg inline-block border ${isEnabled ? 'bg-green-500/15 text-green-300 border-green-500/35' : 'bg-white/5 text-text-muted border-stroke'}`}>
                {isEnabled ? `Включена до ${settings.deadline_time} (${settings.deadline_date})` : 'Выключена'}
              </div>
            </div>
          ) : null}

          {!isEnabled ? (
            <div className="space-y-2">
              {activeCountryTab === 'KG' ? (
                <>
                  <div className="text-xs text-text-muted">Включить доставку — укажите дедлайн (ЧЧ:ММ, Бишкек)</div>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={deadlineInput}
                      onChange={(e) => setDeadlineInput(e.target.value)}
                      className="flex-1 rounded-xl border border-stroke bg-[#161616] px-3 py-2 text-sm text-white outline-none focus:border-white/25"
                    />
                    <button
                      type="button"
                      disabled={enableLoading}
                      onClick={handleEnable}
                      className="h-10 px-5 rounded-xl font-semibold text-xs bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white hover:brightness-110 transition disabled:opacity-60"
                    >
                      {enableLoading ? '…' : 'Включить'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs text-text-muted">Включить доставку для KZ (без дедлайна — выключите вручную)</div>
                  <button
                    type="button"
                    disabled={enableLoading}
                    onClick={handleEnable}
                    className="h-10 w-full rounded-xl font-semibold text-xs bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white hover:brightness-110 transition disabled:opacity-60"
                  >
                    {enableLoading ? '…' : 'Включить доставку'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <button
              type="button"
              disabled={enableLoading}
              onClick={handleDisable}
              className="h-9 px-4 rounded-xl font-semibold text-xs border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition disabled:opacity-60"
            >
              {enableLoading ? '…' : 'Выключить доставку'}
            </button>
          )}

          {enableError ? <div className="mt-2 text-xs text-red-300">{enableError}</div> : null}
        </div>

        {/* Filter by date */}
        <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 mb-4">
          <div className="text-sm font-semibold mb-3">Отчёт по доставкам</div>
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-xl border border-stroke bg-[#161616] px-3 py-2 text-xs text-white outline-none focus:border-white/25"
              placeholder="От"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-xl border border-stroke bg-[#161616] px-3 py-2 text-xs text-white outline-none focus:border-white/25"
              placeholder="До"
            />
            <button
              type="button"
              onClick={loadDeliveries}
              disabled={listLoading}
              className="h-9 px-4 rounded-xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
            >
              <RefreshCw size={14} className={listLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="mb-3">
            <input
              value={deliverySearch}
              onChange={(e) => {
                setDeliverySearch(e.target.value);
                setPage(0);
              }}
              placeholder="Поиск по ФИО или телефону"
              className="w-full rounded-xl border border-stroke bg-[#161616] px-3 py-2 text-sm text-white placeholder:text-white/45 outline-none focus:border-white/25"
            />
          </div>

          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={handleExport}
              disabled={exportLoading}
              className="h-9 px-4 rounded-xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60 flex items-center gap-1.5"
            >
              <FileSpreadsheet size={14} />
              {exportLoading ? 'Экспорт…' : 'Выгрузить в Excel'}
            </button>
          </div>
          {exportError ? <div className="text-xs text-red-300 mb-2">{exportError}</div> : null}
          {exportSuccess ? <div className="text-xs text-green-300 mb-2">{exportSuccess}</div> : null}

          {listError ? <div className="text-xs text-red-300 mb-2">{listError}</div> : null}

          {listLoading && !deliveries.length ? (
            <div className="text-xs text-text-secondary">Загрузка…</div>
          ) : null}

          {!listLoading && !deliveries.length ? (
            <div className="text-xs text-text-secondary">Нет доставок</div>
          ) : null}

          {!listLoading && deliveries.length > 0 && !filteredDeliveries.length ? (
            <div className="text-xs text-text-secondary">Поиск не дал результатов</div>
          ) : null}

          {/* Статистика */}
          {filteredDeliveries.length > 0 ? (() => {
            const now = new Date();
            const todayStr = now.toISOString().slice(0, 10);
            const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
            const weekStr = weekAgo.toISOString().slice(0, 10);
            const todayCount = filteredDeliveries.filter((d) => (d.created_at || '').slice(0, 10) === todayStr).length;
            const weekCount = filteredDeliveries.filter((d) => (d.created_at || '').slice(0, 10) >= weekStr).length;
            const byStatus = {};
            for (const d of filteredDeliveries) { byStatus[d.status] = (byStatus[d.status] || 0) + 1; }
            return (
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="rounded-lg border border-stroke bg-white/5 px-3 py-1.5 text-[11px]">
                  <span className="text-text-muted">Сегодня:</span> <span className="text-text-primary font-semibold">{todayCount}</span>
                </div>
                <div className="rounded-lg border border-stroke bg-white/5 px-3 py-1.5 text-[11px]">
                  <span className="text-text-muted">Неделя:</span> <span className="text-text-primary font-semibold">{weekCount}</span>
                </div>
                {Object.entries(byStatus).map(([st, cnt]) => (
                  <div key={st} className={`rounded-lg border px-3 py-1.5 text-[11px] ${statusColors[st] || 'border-stroke bg-white/5'}`}>
                    {statusIcons[st] || ''} {st}: <span className="font-semibold">{cnt}</span>
                  </div>
                ))}
              </div>
            );
          })() : null}

          <div className="text-xs text-text-muted mb-2">
            Всего: {filteredDeliveries.length}{deliverySearch.trim() ? ` из ${deliveries.length}` : ''}
          </div>

          <div className="space-y-2">
            {filteredDeliveries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((d) => {
              const isUpdating = updatingId === d.id;
              const currentIdx = STATUSES.indexOf(d.status);
              return (
                <div key={d.id} className="rounded-xl border border-stroke bg-white/3 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-lg border ${statusColors[d.status] || ''}`}>
                      {statusIcons[d.status] || ''} {d.status}
                    </span>
                    <span className="text-[11px] text-text-muted">#{d.id}</span>
                  </div>
                  <div className="text-[11px] text-text-secondary space-y-0.5">
                    <div><span className="text-text-muted">ФИО:</span> <span className="text-text-primary">{d.full_name || '—'}</span></div>
                    <div><span className="text-text-muted">Тел:</span> {d.phone_digits ? `+${d.phone_digits}` : '—'}</div>
                    <div><span className="text-text-muted">Адрес:</span> {d.address || '—'}</div>
                    <div><span className="text-text-muted">Регион:</span> <span className="text-text-primary">{d.region || '—'}</span></div>
                    <div><span className="text-text-muted">Дата доставки:</span> {d.delivery_date || '—'}</div>
                    <div className="text-text-muted">Создано: {d.created_at || ''}</div>
                  </div>
                  {/* Кнопки смены статуса */}
                  {d.status !== 'Доставлено' && d.status !== 'Отменена' ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {STATUSES.filter((s) => s !== d.status).map((s) => {
                        const sIdx = STATUSES.indexOf(s);
                        const isPrimary = sIdx === currentIdx + 1;
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={isUpdating}
                            onClick={() => handleStatusChange(d.id, s)}
                            className={`h-7 px-2.5 rounded-lg font-semibold text-[10px] border transition disabled:opacity-60 ${
                              isPrimary
                                ? 'bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white border-transparent'
                                : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'
                            }`}
                          >
                            {statusIcons[s]} {s}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {d.status === 'Отменена' ? (
                    <button
                      type="button"
                      disabled={deletingId === d.id}
                      onClick={async () => {
                        if (!window.confirm('Удалить отменённую доставку #' + d.id + '?')) return;
                        setDeletingId(d.id);
                        try {
                          await apiPost('/api/manager/delivery/delete', { initData: initData?.initData, deliveryId: d.id });
                          setDeliveries((prev) => prev.filter((x) => x.id !== d.id));
                        } catch (e) {
                          setListError(e?.message || 'Ошибка удаления');
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                      className="mt-1.5 w-full py-1.5 rounded-lg text-[11px] font-semibold border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition disabled:opacity-60"
                    >
                      {deletingId === d.id ? 'Удаление…' : 'Удалить'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {filteredDeliveries.length > PAGE_SIZE ? (
            <div className="flex items-center justify-center gap-3 mt-3">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="h-8 px-3 rounded-xl text-xs font-semibold border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-40"
              >
                ← Назад
              </button>
              <span className="text-xs text-text-muted">{page + 1} / {Math.ceil(filteredDeliveries.length / PAGE_SIZE)}</span>
              <button
                type="button"
                disabled={(page + 1) * PAGE_SIZE >= filteredDeliveries.length}
                onClick={() => setPage((p) => p + 1)}
                className="h-8 px-3 rounded-xl text-xs font-semibold border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-40"
              >
                Вперёд →
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ManagerDeliveryPage;

